/**
 * Converts YAML content to data URI format
 * @param yamlContent YAML content to convert
 * @returns YAML content in data URI format
 */
export function yamlToDataUri(yamlContent: string): string {
  try {
    // Ensure YAML content is properly encoded
    // to handle special characters
    const encodedContent = unescape(encodeURIComponent(yamlContent));

    // Convert to base64 using a function that ensures correct padding
    const base64Content = btoa(encodedContent);

    // Check if padding is correct (must end in 0, 1, or 2 '=' characters)
    // If it doesn't end in '=', add necessary padding
    let paddedBase64 = base64Content;
    const remainder = paddedBase64.length % 4;
    if (remainder > 0) {
      paddedBase64 += "=".repeat(4 - remainder);
    }

    // Use exactly the format that works according to the provided example
    return `data:@file/yaml;base64,${paddedBase64}`;
  } catch (error) {
    throw new Error("Could not convert YAML content to data URI");
  }
}

/**
 * Checks if a file is likely a YAML file
 * @param file The file to check
 * @returns true if the file appears to be YAML, false otherwise
 */
export function isYamlFile(file: File): boolean {
  const fileName = file.name.toLowerCase();
  const fileType = file.type.toLowerCase();

  return (
    fileName.endsWith(".yaml") ||
    fileName.endsWith(".yml") ||
    fileType === "application/x-yaml" ||
    fileType === "text/yaml" ||
    fileType === "text/plain" // Many YAMLs are uploaded as text/plain
  );
}

/**
 * Checks if a file is likely a ZIP file
 * @param file The file to check
 * @returns true if the file appears to be ZIP, false otherwise
 */
export function isZipFile(file: File): boolean {
  const fileName = file.name.toLowerCase();
  const fileType = file.type.toLowerCase();

  return fileName.endsWith(".zip") || fileType === "application/zip" || fileType === "application/x-zip-compressed";
}

/**
 * Converts a ZIP file to data URI format
 * @param zipFile The ZIP file to convert
 * @returns Promise that resolves to ZIP content in data URI format
 */
export async function zipToDataUri(zipFile: File): Promise<string> {
  try {
    // Read file content as ArrayBuffer
    const fileBuffer = await zipFile.arrayBuffer();
    const fileBlob = new Blob([fileBuffer], { type: "application/zip" });

    // Convert blob to base64
    return new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => {
        if (typeof reader.result === "string") {
          // Extract only the base64 part of the data URI
          const base64Content = reader.result.split(",")[1];

          // Check if padding is correct (must end in 0, 1, or 2 '=' characters)
          // If it doesn't end in '=', add necessary padding
          let paddedBase64 = base64Content;
          const remainder = paddedBase64.length % 4;
          if (remainder > 0) {
            paddedBase64 += "=".repeat(4 - remainder);
          }

          // Create data URI with the correct format for ZIP
          resolve(`data:application/zip;base64,${paddedBase64}`);
        } else {
          reject(new Error("Error converting ZIP to data URI"));
        }
      };
      reader.onerror = reject;
      reader.readAsDataURL(fileBlob);
    });
  } catch (error) {
    throw new Error("Could not convert ZIP file to data URI");
  }
}

/**
 * Formats a date in a simple readable format without external dependencies
 * @param isoDate ISO date string
 * @returns Formatted date string
 */
export function formatDateSimple(isoDate: string): string {
  try {
    const date = new Date(isoDate);

    if (isNaN(date.getTime())) {
      return isoDate; // If date is invalid, return original string
    }

    const month = date.getMonth() + 1; // getMonth() returns 0-11
    const day = date.getDate();
    const year = date.getFullYear();

    let hours = date.getHours();
    const minutes = date.getMinutes().toString().padStart(2, "0");
    const seconds = date.getSeconds().toString().padStart(2, "0");

    const ampm = hours >= 12 ? "PM" : "AM";
    hours = hours % 12;
    hours = hours ? hours : 12; // Hour '0' should be '12'

    return `${month}/${day}/${year} ${hours}:${minutes}:${seconds}${ampm}`;
  } catch (error) {
    return isoDate;
  }
}

/**
 * Returns a user-friendly display name for a given API manager name.
 * If the name is not recognized or is undefined, it defaults to "Apigee".
 *
 * @param name - The raw name of the target API manager.
 * @returns A display-friendly version of the API manager name.
 */
export function getApiManagerDisplayName(name?: string): string {
  if (!name) return "Apigee X";

  const normalized = name.trim().toLowerCase();

  const nameMap: Record<string, string> = {
    apigee: "Apigee X",
    ibm: "IBM API Connect",
  };

  return nameMap[normalized] || name;
}
